import { ButtonGroup, styled } from '@mui/material';
import { forwardRef } from 'react';
import Badge from 'shell/components/Badge';
import Button from 'shell/components/Button';
import Icon from 'shell/components/Icon';

export const ButtonGroupSelectedVariant = Object.freeze({
    PRIMARY: 'primary',
    SUBTLE: 'subtle',
});

const BUTTON_GROUP_SELECTED_VARIANTS = Object.values(ButtonGroupSelectedVariant);

const getSelectedVariantStyles = (theme, selectedVariant) => {
    return {
        primary: {},
        subtle: {
            background: theme.palette.ensightful.shades[0],
            color: theme.palette.ensightful.primary[500],
            '&:hover': {
                background: theme.palette.ensightful.shades[0],
                color: theme.palette.ensightful.neutral[700],
            },
            '&:active': {
                background: theme.palette.ensightful.neutral[400],
            },
        },
    }[selectedVariant];
};

const CustomButtonGroup = styled(ButtonGroup, {
    shouldForwardProp: (prop) => prop !== 'numOptions' && prop !== 'maxWidth' && prop !== 'iconOnly',
})(({ theme, iconOnly, maxWidth, numOptions }) => {
    const gap = theme.spacing(1);
    return {
        gap,
        borderRadius: '40px',
        padding: theme.spacing(1),
        background: theme.palette.ensightful.neutral[200],
        '& > *': {
            width: `calc((100% - ${numOptions - 1} * ${gap}) / ${numOptions})`,
            minWidth: 0,
            display: 'flex',
        },
        '& .MuiButton-root': {
            flex: 1,
        },
        '& .MuiBox-root': {
            flex: 1,
            width: '100%',
        },
        ...(iconOnly && {
            width: 'fit-content',
        }),
        ...(maxWidth && {
            maxWidth,
            width: '100%',
        }),
    };
});

const CustomBadge = styled(Badge)(({ theme }) => {
    return {
        '& .MuiBadge-dot': {
            borderRadius: '50%',
            width: '12px',
            height: '12px',
            background: theme.palette.ensightful.error[600],
            top: '4px',
            right: '4px',
        },
    };
});

const BadgeButton = styled(Button, {
    shouldForwardProp: (prop) =>
        prop !== 'isSelected' && prop !== 'hasMaxWidth' && prop !== 'iconOnly' && prop !== 'selectedVariant',
})(({ theme, isSelected, hasMaxWidth, iconOnly, selectedVariant }) => ({
    '&&': {
        width: '100%',

        borderRadius: '40px',
        ...(isSelected && getSelectedVariantStyles(theme, selectedVariant)),
        ...(iconOnly && {
            paddingInline: theme.spacing(2),
        }),
        ...(hasMaxWidth && {
            paddingInline: theme.spacing(2),
        }),
    },
    ...(hasMaxWidth && {
        fontSize: 'clamp(0.6rem, 2vw, 0.875rem)',
        '& .MuiButton-startIcon': {
            marginRight: theme.spacing(0.5),
        },
    }),
    '&:focus-visible': {
        outlineColor: isSelected ? theme.palette.ensightful.secondary[100] : theme.palette.ensightful.secondary[400],
        outlineWidth: '3px',
        outlineOffset: '-5px',
    },
    '& > div': {
        width: '100%',
        flex: '0 1 auto',
        minWidth: 0,
        marginLeft: theme.spacing(1),
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        display: 'block',

        ...(iconOnly && {
            display: 'none',
        }),
    },
}));

const CustomButton = styled(Button, {
    shouldForwardProp: (prop) =>
        prop !== 'isSelected' && prop !== 'hasMaxWidth' && prop !== 'iconOnly' && prop !== 'selectedVariant',
})(({ theme, isSelected, hasMaxWidth, iconOnly, selectedVariant }) => ({
    '&&': {
        borderRadius: '40px',
        ...(isSelected && getSelectedVariantStyles(theme, selectedVariant)),
        ...(iconOnly && {
            paddingInline: theme.spacing(2),
        }),
        ...(hasMaxWidth && {
            paddingInline: theme.spacing(2),
        }),
    },
    ...(hasMaxWidth && {
        fontSize: 'clamp(0.6rem, 2vw, 0.875rem)',
        '& .MuiButton-startIcon': {
            marginRight: theme.spacing(0.5),
        },
    }),
    '&:focus-visible': {
        outlineColor: isSelected ? theme.palette.ensightful.secondary[100] : theme.palette.ensightful.secondary[400],
        outlineWidth: '3px',
        outlineOffset: '-5px',
    },
    '& > div': {
        width: '100%',
        flex: '0 1 auto',
        minWidth: 0,
        marginLeft: theme.spacing(1),
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        display: 'block',

        ...(iconOnly && {
            display: 'none',
        }),
    },
}));

export default forwardRef(
    (
        {
            iconOnly = false,
            maxWidth,
            options,
            selected,
            selectedVariant = ButtonGroupSelectedVariant.PRIMARY,
            onChange,
            ...rest
        },
        ref
    ) => {
        if (!options.length) throw new Error(`Shell: options cannot be empty in component ButtonGroup.`);
        if (!selected) throw new Error(`Shell: selected cannot be null in component ButtonGroup.`);
        if (!BUTTON_GROUP_SELECTED_VARIANTS.includes(selectedVariant)) {
            throw new Error(`Shell: Invalid selectedVariant "${selectedVariant}" in component ButtonGroup.`);
        }
        if (iconOnly && options.some((option) => !option.icon)) {
            throw new Error(`Shell: All options must include an icon when iconOnly is true in component ButtonGroup.`);
        }

        const hasMaxWidth = Boolean(maxWidth);

        return (
            <CustomButtonGroup
                ref={ref}
                {...rest}
                disableElevation
                iconOnly={iconOnly}
                maxWidth={maxWidth}
                numOptions={options.length}
            >
                {options.map((option) => {
                    const isSelected = selected === option.key;
                    const startIcon = option.icon ? <Icon kind={option.icon} /> : undefined;
                    const ButtonComponent = option.badge ? BadgeButton : CustomButton;

                    const button = (
                        <ButtonComponent
                            key={option.key}
                            aria-label={iconOnly ? option.value : undefined}
                            color={isSelected ? 'primary' : 'neutral'}
                            disabled={option.disabled}
                            hasMaxWidth={hasMaxWidth}
                            iconOnly={iconOnly}
                            isSelected={isSelected}
                            selectedVariant={selectedVariant}
                            startIcon={startIcon}
                            tooltip={option.tooltip ? { component: option.tooltip } : undefined}
                            variant={isSelected ? 'primary' : 'tertiary'}
                            onClick={() => onChange(option.key)}
                        >
                            {option.value}
                        </ButtonComponent>
                    );

                    return option.badge ? (
                        <CustomBadge key={option.key} variant={'dot'}>
                            {button}
                        </CustomBadge>
                    ) : (
                        button
                    );
                })}
            </CustomButtonGroup>
        );
    }
);
